import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CheckCircle, Clock, Receipt, FileText, Image, FileDown, Filter,
  Calendar, BarChart3, Pencil, X, Archive
} from "lucide-react";
import { SignatureCell } from "./SignatureCell";
import { useAdmin } from "./AdminContext";
import { MonthDropdown } from "./MonthDropdown";

function StatCard({ icon, label, value, testId }) {
  return (
    <motion.div whileHover={{ scale: 1.02 }} className="stat-card rounded-lg" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div><p className="font-mono text-xs uppercase tracking-wider text-vault-gold/80 mb-2">{label}</p><p className="font-outfit text-4xl font-bold text-vault-text">{value}</p></div>
        <div className="text-vault-gold/50">{icon}</div>
      </div>
    </motion.div>
  );
}

export function BillingTab() {
  const { API, records, startDate, endDate, setStartDate, setEndDate, isFiltered, handleApplyFilter, handleClearFilter, fetchData } = useAdmin();
  const [billingSummary, setBillingSummary] = useState(null);
  const [billingSummaryLoading, setBillingSummaryLoading] = useState(false);
  const [showBillingSummary, setShowBillingSummary] = useState(false);
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Billing is bucketed by check-out date (matches the railroad billing model).
  // Re-fetch on mount so we never display stale records left over from other tabs
  // (e.g. SignInTab pins the shared `records` to check_in_date).
  useEffect(() => {
    fetchData(startDate || null, endDate || null, 0, 'check_out_date');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditForm({
      employee_number: record.employee_number || "",
      employee_name: record.employee_name || "",
      room_number: record.room_number || "",
      check_in_date: record.check_in_date || "",
      check_in_time: record.check_in_time || "",
      check_out_date: record.check_out_date || "",
      check_out_time: record.check_out_time || "",
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRecord) return;
    try {
      await axios.put(`${API}/admin/records/${editingRecord.id}`, editForm);
      toast.success("Record updated successfully");
      setEditingRecord(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update record");
    }
  };

  const fetchBillingSummary = async () => {
    setBillingSummaryLoading(true);
    try {
      let url = `${API}/admin/billing-summary`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url);
      setBillingSummary(response.data);
      setShowBillingSummary(true);
    } catch (error) { toast.error("Failed to load billing summary"); }
    finally { setBillingSummaryLoading(false); }
  };

  const downloadBillingSummary = async () => {
    try {
      let url = `${API}/admin/export-billing-summary`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = downloadUrl; a.download = 'hodler_inn_daily_billing.xlsx'; a.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success("Billing summary exported!");
    } catch (error) { toast.error("Failed to export billing summary"); }
  };

  const fetchRevenueAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      let url = `${API}/admin/internal/revenue-analytics`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url);
      setRevenueAnalytics(response.data);
      setShowAnalytics(true);
    } catch (error) { toast.error("Failed to load analytics"); }
    finally { setAnalyticsLoading(false); }
  };

  const handleExportBilling = async () => {
    try {
      let url = `${API}/admin/export-billing`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = blobUrl;
      link.setAttribute("download", startDate || endDate ? `hodler_inn_billing_${startDate || 'start'}_to_${endDate || 'end'}.xlsx` : "hodler_inn_billing_report.xlsx");
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(blobUrl);
      toast.success("Billing Report (Excel) exported!");
    } catch (error) { toast.error("Failed to export"); }
  };

  const handleExportBillingPng = async () => {
    try {
      let url = `${API}/admin/export-billing-png`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = blobUrl;
      link.setAttribute("download", startDate || endDate ? `hodler_inn_billing_${startDate || 'start'}_to_${endDate || 'end'}.png` : "hodler_inn_billing_report.png");
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(blobUrl);
      toast.success("Billing Report (PNG) exported!");
    } catch (error) { toast.error("Failed to export"); }
  };

  const handleExportBillingPdf = async () => {
    try {
      let url = `${API}/admin/export-billing-pdf`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = blobUrl; link.setAttribute("download", "hodler_inn_billing_report.pdf");
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(blobUrl);
      toast.success("Billing Report (PDF) exported!");
    } catch (error) { toast.error("Failed to export PDF"); }
  };

  // Bundle both Billing PDF + Sign-In Sheet PDF for the railroad in a single ZIP.
  // Uses the currently applied date filter (start/end) — server applies the same
  // range to both reports (billing buckets by check-out, sign-in by check-in).
  const handleExportRailroadZip = async () => {
    try {
      if (!startDate || !endDate) {
        toast.error("Please apply a Start and End date filter before downloading the railroad ZIP.");
        return;
      }
      toast.info("Generating ZIP… this can take 10–30 seconds for a full month. Please don't close this tab.");
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const response = await axios.get(`${API}/admin/export-railroad-zip?${params.toString()}`, {
        responseType: "blob",
        timeout: 180000, // 3 min — plenty of headroom for a full-month bundle through the ingress
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Hodler_Inn_Railroad_${startDate}_to_${endDate}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Railroad ZIP downloaded!");
    } catch (error) {
      const msg = error?.code === "ECONNABORTED"
        ? "Download timed out. Try a smaller date range or retry."
        : "Failed to generate Railroad ZIP";
      toast.error(msg);
    }
  };

  const completedRecords = records.filter(r => r.is_checked_out);

  return (
    <>
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight">Billing Report</h1>
            <p className="text-vault-text-secondary font-manrope mt-1">View completed stays and billing details</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleExportBilling} className="vault-btn-primary flex items-center gap-2" data-testid="export-billing-excel-btn"><FileText className="w-4 h-4" />Excel</Button>
            <Button onClick={handleExportBillingPng} className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2" data-testid="export-billing-png-btn"><Image className="w-4 h-4" />PNG</Button>
            <Button onClick={handleExportBillingPdf} className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2" data-testid="export-billing-pdf-btn"><FileDown className="w-4 h-4" />PDF</Button>
            <Button
              onClick={handleExportRailroadZip}
              className="bg-vault-gold hover:bg-amber-500 text-black font-semibold flex items-center gap-2"
              data-testid="export-railroad-zip-btn"
              title="Bundle Billing + Sign-In PDFs for the railroad (uses the date filter above)"
            >
              <Archive className="w-4 h-4" />Railroad ZIP
            </Button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3 bg-vault-surface-highlight/50 p-3 rounded-lg border border-vault-border">
          <Filter className="w-4 h-4 text-vault-gold" /><span className="text-vault-text-secondary text-sm font-medium">Filter:</span>
          <MonthDropdown dateFieldOverride="check_out_date" />
          <span className="text-vault-text-secondary text-sm">or custom:</span>
          <DatePicker date={startDate} onDateChange={setStartDate} placeholder="Start Date" className="h-9" /><span className="text-vault-text-secondary">to</span>
          <DatePicker date={endDate} onDateChange={setEndDate} placeholder="End Date" className="h-9" />
          <Button onClick={handleApplyFilter} className="vault-btn-primary h-9 px-4">Apply</Button>
          {isFiltered && <Button onClick={handleClearFilter} variant="ghost" className="h-9 px-3 text-vault-text-secondary hover:text-vault-gold"><X className="w-4 h-4 mr-1" />Clear</Button>}
        </div>
        <div className="mt-2 text-sm text-vault-text-secondary" data-testid="billing-records-count-label">
          Showing <span className="text-vault-gold font-medium">{completedRecords.length}</span> completed of <span className="text-vault-text">{records.length}</span> records
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <StatCard icon={<CheckCircle className="w-6 h-6" />} label="Completed Stays" value={completedRecords.length} testId="billing-completed" />
        <StatCard icon={<Clock className="w-6 h-6" />} label="Total Hours" value={completedRecords.reduce((sum, r) => sum + (r.total_hours || 0), 0).toFixed(1) + "h"} testId="billing-hours" />
        <StatCard icon={<Receipt className="w-6 h-6" />} label="Total Nights Billed" value={completedRecords.reduce((sum, r) => sum + (r.total_nights || 0), 0)} testId="billing-nights" />
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <Button onClick={fetchBillingSummary} disabled={billingSummaryLoading} className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2" data-testid="daily-summary-btn"><Calendar className="w-4 h-4" />{billingSummaryLoading ? "Loading..." : "Daily Summary (Rate + Tax)"}</Button>
        <Button onClick={fetchRevenueAnalytics} disabled={analyticsLoading} className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2" data-testid="analytics-btn"><BarChart3 className="w-4 h-4" />{analyticsLoading ? "Loading..." : "Revenue Analytics (Internal)"}</Button>
      </div>

      {showBillingSummary && billingSummary && (
        <Card className="bg-vault-surface border-vault-border mb-6" data-testid="daily-summary-card">
          <CardHeader className="border-b border-vault-border">
            <div className="flex justify-between items-center">
              <CardTitle className="font-outfit text-xl text-vault-text flex items-center gap-2"><Calendar className="w-5 h-5 text-blue-400" />Daily Billing Summary</CardTitle>
              <div className="flex gap-2">
                <Button onClick={downloadBillingSummary} size="sm" className="bg-green-600 hover:bg-green-700 text-white"><FileDown className="w-4 h-4 mr-1" />Export Excel</Button>
                <Button onClick={() => setShowBillingSummary(false)} size="sm" variant="ghost" className="text-vault-text-secondary"><X className="w-4 h-4" /></Button>
              </div>
            </div>
            <p className="text-sm text-vault-text-secondary mt-1">Railroad Rate: ${billingSummary.rate_info?.base_rate?.toFixed(2) || '56.00'} + Tax ({billingSummary.rate_info?.tax_rate_percent?.toFixed(2) || '10.30'}%) = ${billingSummary.rate_info?.total_per_night?.toFixed(2) || '61.77'}/night</p>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[300px]">
              <Table>
                <TableHeader><TableRow className="table-header border-vault-border hover:bg-transparent"><TableHead className="text-blue-400">Date</TableHead><TableHead className="text-blue-400">Rooms</TableHead><TableHead className="text-blue-400">Nights</TableHead><TableHead className="text-blue-400">Rate (${billingSummary.rate_info?.base_rate?.toFixed(0) || '56'})</TableHead><TableHead className="text-blue-400">Tax ({billingSummary.rate_info?.tax_rate_percent?.toFixed(2) || '10.30'}%)</TableHead><TableHead className="text-blue-400">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {billingSummary.daily_summary?.map((day, idx) => (
                    <TableRow key={idx} className="table-row border-vault-border">
                      <TableCell className="font-mono text-vault-text">{day.date}</TableCell><TableCell className="text-vault-text">{day.rooms_billed}</TableCell><TableCell className="text-vault-text font-medium">{day.billable_nights}</TableCell><TableCell className="text-green-400 font-mono">${day.rate?.toFixed(2)}</TableCell><TableCell className="text-amber-400 font-mono">${day.tax?.toFixed(2)}</TableCell><TableCell className="text-vault-gold font-bold font-mono">${day.total?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-vault-surface-highlight border-vault-border">
                    <TableCell className="font-bold text-vault-text">TOTAL</TableCell><TableCell className="font-bold text-vault-text">{billingSummary.totals?.total_rooms}</TableCell><TableCell className="font-bold text-vault-text">{billingSummary.totals?.total_nights}</TableCell><TableCell className="font-bold text-green-400 font-mono">${billingSummary.totals?.total_rate?.toFixed(2)}</TableCell><TableCell className="font-bold text-amber-400 font-mono">${billingSummary.totals?.total_tax?.toFixed(2)}</TableCell><TableCell className="font-bold text-vault-gold font-mono">${billingSummary.totals?.total_amount?.toFixed(2)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {showAnalytics && revenueAnalytics && (
        <Card className="bg-vault-surface border-purple-500/30 mb-6" data-testid="analytics-card">
          <CardHeader className="border-b border-vault-border">
            <div className="flex justify-between items-center">
              <CardTitle className="font-outfit text-xl text-vault-text flex items-center gap-2"><BarChart3 className="w-5 h-5 text-purple-400" />Revenue Analytics<span className="text-xs bg-purple-600/30 text-purple-300 px-2 py-0.5 rounded">INTERNAL USE ONLY</span></CardTitle>
              <Button onClick={() => setShowAnalytics(false)} size="sm" variant="ghost" className="text-vault-text-secondary"><X className="w-4 h-4" /></Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3"><p className="text-xs text-purple-300">Total Stays</p><p className="text-xl font-bold text-vault-text">{revenueAnalytics.summary?.total_stays}</p></div>
              <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3"><p className="text-xs text-purple-300">Total Hours</p><p className="text-xl font-bold text-vault-text">{revenueAnalytics.summary?.total_hours?.toFixed(1)}h</p></div>
              <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3"><p className="text-xs text-purple-300">Total Revenue</p><p className="text-xl font-bold text-green-400">${revenueAnalytics.summary?.total_revenue?.toFixed(2)}</p></div>
              <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3"><p className="text-xs text-purple-300">Avg $/Hour</p><p className="text-xl font-bold text-vault-gold">${revenueAnalytics.summary?.avg_revenue_per_hour?.toFixed(2)}</p></div>
            </div>
            <h4 className="text-vault-text-secondary text-sm font-medium mb-2">Daily Revenue Breakdown</h4>
            <ScrollArea className="h-[200px]">
              <Table>
                <TableHeader><TableRow className="table-header border-vault-border hover:bg-transparent"><TableHead className="text-purple-400">Date</TableHead><TableHead className="text-purple-400">Stays</TableHead><TableHead className="text-purple-400">Hours</TableHead><TableHead className="text-purple-400">Revenue</TableHead><TableHead className="text-purple-400">$/Hour</TableHead></TableRow></TableHeader>
                <TableBody>
                  {revenueAnalytics.daily_summary?.map((day, idx) => (
                    <TableRow key={idx} className="table-row border-vault-border"><TableCell className="font-mono text-vault-text">{day.date}</TableCell><TableCell className="text-vault-text">{day.stays}</TableCell><TableCell className="text-vault-text">{day.total_hours?.toFixed(1)}h</TableCell><TableCell className="text-green-400 font-mono">${day.total_revenue?.toFixed(2)}</TableCell><TableCell className="text-vault-gold font-mono">${day.avg_revenue_per_hour?.toFixed(2)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      <div className="mb-4 flex items-center gap-4 text-sm">
        <span className="text-vault-text-secondary font-medium">Legend:</span>
        <div className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-500/20 border border-blue-500/50 rounded"></div><span className="text-vault-text-secondary">2+ Night Stay</span></div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 bg-vault-surface border border-vault-border rounded"></div><span className="text-vault-text-secondary">1 Night Stay</span></div>
      </div>

      <Card className="bg-vault-surface border-vault-border" data-testid="billing-table-card">
        <CardHeader className="border-b border-vault-border"><CardTitle className="font-outfit text-xl text-vault-text flex items-center gap-2"><Receipt className="w-5 h-5 text-vault-gold" />Billing Details</CardTitle></CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader><TableRow className="table-header border-vault-border hover:bg-transparent"><TableHead className="text-vault-gold">#</TableHead><TableHead className="text-vault-gold">Name</TableHead><TableHead className="text-vault-gold">Employee ID</TableHead><TableHead className="text-vault-gold">Room #</TableHead><TableHead className="text-vault-gold">Check-In</TableHead><TableHead className="text-vault-gold">Check-Out</TableHead><TableHead className="text-vault-gold">Total Hours</TableHead><TableHead className="text-vault-gold">Nights Billed</TableHead><TableHead className="text-vault-gold">Signed</TableHead><TableHead className="text-vault-gold">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {completedRecords.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center text-vault-text-secondary py-8">No completed stays found</TableCell></TableRow>
                ) : completedRecords.map((record, index) => (
                  <TableRow key={record.id} className={`table-row border-vault-border ${record.total_nights >= 2 ? 'bg-blue-500/10 hover:bg-blue-500/20' : ''}`} data-testid={`billing-row-${record.id}`}>
                    <TableCell className="font-mono text-vault-text">{index + 1}</TableCell>
                    <TableCell className="text-vault-text font-medium">{record.employee_name}</TableCell>
                    <TableCell className="font-mono text-vault-text">{record.employee_number}</TableCell>
                    <TableCell className="font-mono text-vault-gold font-bold">{record.room_number}</TableCell>
                    <TableCell className="text-vault-text-secondary text-sm">{record.check_in_date} {record.check_in_time}</TableCell>
                    <TableCell className="text-vault-text-secondary text-sm">{record.check_out_date} {record.check_out_time}</TableCell>
                    <TableCell className="font-mono text-vault-text">{record.total_hours}h</TableCell>
                    <TableCell className={`font-mono font-bold text-lg ${record.total_nights >= 2 ? 'text-blue-400' : 'text-vault-gold'}`}>{record.total_nights}</TableCell>
                    <TableCell><SignatureCell record={record} API={API} /></TableCell>
                    <TableCell><button onClick={() => handleEdit(record)} className="p-1.5 rounded hover:bg-vault-surface-highlight text-vault-text-secondary hover:text-vault-gold transition-colors" title="Edit record" data-testid={`billing-edit-${record.id}`}><Pencil className="w-4 h-4" /></button></TableCell>
                  </TableRow>
                ))}
                {completedRecords.length > 0 && (
                  <TableRow className="bg-vault-surface-highlight border-vault-border">
                    <TableCell colSpan={6} className="text-right font-bold text-vault-text">TOTAL</TableCell>
                    <TableCell className="font-mono text-vault-text font-bold">{completedRecords.reduce((sum, r) => sum + (r.total_hours || 0), 0).toFixed(1)}h</TableCell>
                    <TableCell className="font-mono text-vault-gold font-bold text-lg">{completedRecords.reduce((sum, r) => sum + (r.total_nights || 0), 0)}</TableCell>
                    <TableCell></TableCell><TableCell></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      <Dialog open={!!editingRecord} onOpenChange={(open) => { if (!open) setEditingRecord(null); }}>
        <DialogContent className="bg-vault-surface border-vault-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-vault-text">Edit Billing Record</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-vault-text-secondary text-sm">Employee Name</Label>
              <Input data-testid="edit-employee-name" className="bg-vault-surface-highlight border-vault-border text-vault-text mt-1" value={editForm.employee_name || ""} onChange={(e) => setEditForm({ ...editForm, employee_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-vault-text-secondary text-sm">Employee ID</Label>
              <Input data-testid="edit-employee-number" className="bg-vault-surface-highlight border-vault-border text-vault-text mt-1" value={editForm.employee_number || ""} onChange={(e) => setEditForm({ ...editForm, employee_number: e.target.value })} />
            </div>
            <div>
              <Label className="text-vault-text-secondary text-sm">Room Number</Label>
              <Input data-testid="edit-room-number" className="bg-vault-surface-highlight border-vault-border text-vault-text mt-1" value={editForm.room_number || ""} onChange={(e) => setEditForm({ ...editForm, room_number: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-vault-text-secondary text-sm">Check-In Date</Label>
                <Input data-testid="edit-checkin-date" type="date" className="bg-vault-surface-highlight border-vault-border text-vault-text mt-1" value={editForm.check_in_date || ""} onChange={(e) => setEditForm({ ...editForm, check_in_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-vault-text-secondary text-sm">Check-In Time</Label>
                <Input data-testid="edit-checkin-time" type="time" className="bg-vault-surface-highlight border-vault-border text-vault-text mt-1" value={editForm.check_in_time || ""} onChange={(e) => setEditForm({ ...editForm, check_in_time: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-vault-text-secondary text-sm">Check-Out Date</Label>
                <Input data-testid="edit-checkout-date" type="date" className="bg-vault-surface-highlight border-vault-border text-vault-text mt-1" value={editForm.check_out_date || ""} onChange={(e) => setEditForm({ ...editForm, check_out_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-vault-text-secondary text-sm">Check-Out Time</Label>
                <Input data-testid="edit-checkout-time" type="time" className="bg-vault-surface-highlight border-vault-border text-vault-text mt-1" value={editForm.check_out_time || ""} onChange={(e) => setEditForm({ ...editForm, check_out_time: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="ghost" onClick={() => setEditingRecord(null)} className="text-vault-text-secondary" data-testid="edit-cancel-btn">Cancel</Button>
            <Button onClick={handleSaveEdit} className="vault-btn-primary" data-testid="edit-save-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
